/**
 * MessageContextMenu (06 §4.12) — Reply, Forward, Copy, Info, Delete.
 * Delete opens a confirm dialog with Delete-for-me / Delete-for-everyone.
 */
import { useState } from 'react';
import { Reply, Forward, Copy, Info, Trash2 } from 'lucide-react';
import type { Message } from '../../data/model';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { Dialog } from '../primitives/Overlays';
import styles from './MessageContextMenu.module.css';

interface Props {
  message: Message;
  x: number;
  y: number;
  onClose: () => void;
}

export function MessageContextMenu({ message, x, y, onClose }: Props) {
  const me = useChatStore((s) => s.me);
  const setReplyTo = useChatStore((s) => s.setReplyTo);
  const deleteForMe = useChatStore((s) => s.deleteForMe);
  const deleteForEveryone = useChatStore((s) => s.deleteForEveryone);
  const toast = useUiStore((s) => s.toast);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isMine = message.senderId === me.id;
  // Delete-for-everyone window ~2 days (06 §4.12, Open Q3).
  const withinWindow = Date.now() - message.sentAtMs < 2 * 24 * 3600_000;

  return (
    <>
      <div className={styles.scrim} onClick={onClose} />
      <ul className={styles.menu} style={{ top: y, left: x }} role="menu">
        <li>
          <button role="menuitem" onClick={() => { setReplyTo(message.conversationId, message); onClose(); }}>
            <Reply size={17} /> Reply
          </button>
        </li>
        <li>
          <button role="menuitem" onClick={() => { toast('Forwarding…'); onClose(); }}>
            <Forward size={17} /> Forward
          </button>
        </li>
        {message.text && (
          <li>
            <button
              role="menuitem"
              onClick={() => {
                void navigator.clipboard?.writeText(message.text);
                toast('Copied');
                onClose();
              }}
            >
              <Copy size={17} /> Copy
            </button>
          </li>
        )}
        {isMine && (
          <li>
            <button role="menuitem" onClick={() => { toast('Delivered & read times'); onClose(); }}>
              <Info size={17} /> Info
            </button>
          </li>
        )}
        <li>
          <button role="menuitem" className={styles.danger} onClick={() => setConfirmDelete(true)}>
            <Trash2 size={17} /> Delete
          </button>
        </li>
      </ul>

      <Dialog open={confirmDelete} onClose={() => { setConfirmDelete(false); onClose(); }} title="Delete message?">
        <h3 className={styles.dialogTitle}>Delete message?</h3>
        <div className={styles.dialogActions}>
          {isMine && withinWindow && (
            <button
              className={styles.deleteAll}
              onClick={() => {
                deleteForEveryone(message.conversationId, message.clientMsgId);
                toast('Message deleted for everyone');
                setConfirmDelete(false);
                onClose();
              }}
            >
              Delete for everyone
            </button>
          )}
          <button
            className={styles.deleteMe}
            onClick={() => {
              deleteForMe(message.conversationId, message.clientMsgId);
              toast('Message deleted');
              setConfirmDelete(false);
              onClose();
            }}
          >
            Delete for me
          </button>
          <button className={styles.cancelBtn} onClick={() => { setConfirmDelete(false); onClose(); }}>
            Cancel
          </button>
        </div>
      </Dialog>
    </>
  );
}
